// ** React Imports
import { useState, useContext, useMemo, useRef, useEffect } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";
import { setUserLimit } from "store/modules/user-profile/user-profile-main.slice";

// ** Custom Components
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardBody } from "components/layout/Card/CardBody";
import Text from "components/typography/Text";
import DatePresenter from "components/other/DatePresenter";
import UserMediaSkeletor from "components/user/UserMediaSkeletor";
import DynamicLockIcon from "components/icons/DynamicLockIcon";
import Block from "components/layout/Block";
import { StyledMediaTypePaddingContainerMobile } from "components/media/styles";

// ** Media Components
import MediaType from "../../media/MediaType";
import MediaTypeGalleryRatio4to3 from "components/media/MediaTypeGallery_Ratio_4_3";
import MediaPostProfileInteractions from "./components/MediaPostProfileInteractions";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import convertText from "utils/convertText";
import sharedObservers from "utils/sharedObservers";

// ** Routes
import containers from "containers";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const MediaPostProfile = ({ item, user }) => {
	const isAuth = useSelector((state) => state.globalCommon.isAuth);
	const { isPhone } = useContext(deviceTypeContext);

	const [isRemoved, setIsRemoved] = useState(false);
	const [inView, setInView] = useState(true);

	const type = item.media_type !== undefined ? item.media_type : "post";
	const to = generatePath(containers.modal.path, {
		contentId: item.id,
		login: user.login?.toLowerCase(),
		type,
	});

	// #####################################################

	const vipPropsMobile = useMemo(
		() => ({
			height: 14,
			marginBottom: 0.125,
		}),
		[],
	);

	const contentMobile = useMemo(
		() => (
			<Text small color={"grey"}>
				<DatePresenter date={item.date_add} onlyDate />
				{item.private && (
					<DynamicLockIcon
						customMargin="0 0 0 0.5rem"
						customHeight="11px"
					/>
				)}
			</Text>
		),
		[item.date_add, item.private],
	);

	const memoizedUserMediaSkeletorMobile = useMemo(
		() => (
			<UserMediaSkeletor
				noMarginBottom
				alignCenter
				user={user}
				size="small"
				color="primary"
				content={contentMobile}
				vipProps={vipPropsMobile}
			/>
		),
		[user, contentMobile, vipPropsMobile],
	);

	const vipProps = useMemo(
		() => ({
			height: 14,
			marginBottom: 0.125,
		}),
		[],
	);

	const content = useMemo(
		() => (
			<Text small color="grey">
				<DatePresenter date={item.date_add} onlyDate />
				{item.private ? (
					<DynamicLockIcon
						customMargin="0 0 0 0.5rem"
						customHeight="11px"
					/>
				) : null}
			</Text>
		),
		[item.date_add, item.private],
	);

	const memoizedUserMediaSkeletor = useMemo(
		() => (
			<UserMediaSkeletor
				alignCenter
				user={user}
				size="small"
				content={content}
				vipProps={vipProps}
			/>
		),
		[user, content, vipProps],
	);

	const containerRef = useRef(null);
	const lastHeightRef = useRef(null);

	useEffect(() => {
		if (!containerRef.current) return;

		const cleanupIntersection = sharedObservers.observeIntersection(
			containerRef.current,
			(isIntersecting) => {
				setInView(isIntersecting);
			},
		);

		const cleanupResize = sharedObservers.observeResize(
			containerRef.current,
			(height) => {
				if (lastHeightRef.current !== height) {
					lastHeightRef.current = height;
				}
			},
		);

		return () => {
			cleanupIntersection();
			cleanupResize();
		};
	}, [item.id]);

	// ############

	if (isRemoved) {
		return null;
	}

	return (
		<CardWrapper
			ref={containerRef}
			style={{
				minHeight: inView
					? "auto"
					: lastHeightRef.current > 0
						? lastHeightRef.current
						: "148px", // single line post height
			}}
		>
			<CardBody isMediaPostCard>
				{inView && (
					<>
						{isPhone ? (
							<Block flex column withRowGap mb={3}>
								<Block mx={4}>
									{memoizedUserMediaSkeletorMobile}
								</Block>
								{(item.media_type === "photo" ||
									item.media_type === "video") && (
									<MediaTypeGalleryRatio4to3
										type={type}
										item={item}
										isAuth={isAuth}
										isPhone={isPhone}
										size="large_post"
										to={to}
									/>
								)}
								{item.description && (
									<Block flex column>
										<StyledMediaTypePaddingContainerMobile>
											{convertText({
												pieces: item.description,
											})}
										</StyledMediaTypePaddingContainerMobile>
									</Block>
								)}
							</Block>
						) : (
							<MediaType
								type={type}
								item={item}
								isAuth={isAuth}
								isPhone={isPhone}
								to={to}
								isProfile={true}
								size="large_post"
								userLogin={memoizedUserMediaSkeletor}
							/>
						)}
					</>
				)}
				<MediaPostProfileInteractions
					itemId={item?.id}
					itemLimit={item?.limit}
					itemCanLike={item?.can_like}
					itemILiked={item?.i_liked}
					itemLikesCount={item?.likes}
					itemIComment={item?.i_comment}
					itemCanComment={item?.can_comment || item?.canComment}
					itemCommentsCount={item?.comments}
					itemCommentsArray={item?.comments_list}
					itemMediaType={item?.media_type}
					itemFilterComment={
						item?.filter_comment || item?.filterComment
					}
					userId={user?.id}
					setUserLimit={setUserLimit}
					setIsRemoved={setIsRemoved}
					inView={inView}
				/>
			</CardBody>
		</CardWrapper>
	);
};

// #####################################################

export default MediaPostProfile;
